import { z } from 'zod';

const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;

// =============================================================================
// Onboarding Form Schema — "Apply for Credit"
// =============================================================================

export const formSubmissionSchema = z.object({
  // Partner tracking (pre-filled via URL, required for Left Main sync)
  partnerSubmissionId: z.string().min(1, 'Partner submission ID is required'),

  // Applicant
  fullName: z.string().min(1, 'Full name is required').max(200),
  entityName: z.string().min(1, 'LLC entity name is required').max(200),
  dba: z.string().max(200).optional().or(z.literal('')),

  // Contact
  email: z.string().email('Valid email is required'),
  phone: z.string().regex(phoneRegex, 'Valid phone number is required'),

  // Mailing Address
  mailingAddress1: z.string().min(1, 'Mailing address is required').max(200),
  mailingAddress2: z.string().max(200).optional().or(z.literal('')),
  mailingCity: z.string().min(1, 'City is required').max(100),
  mailingState: z.string().length(2, 'State is required'),
  mailingZip: z.string().min(5, 'ZIP code is required').max(10),

  // Credit Request
  locAmountRequested: z.number().positive('Line of credit amount must be positive'),
  marketsOperating: z.string().min(1, 'Market selection is required'),
  avgAnnualTransactions: z.number().int().positive('Annual transactions must be a positive number'),
  avgPurchasePrice: z.number().positive('Average purchase price must be positive'),

  // Preferences
  preferredTitleCompany: z.string().max(200).optional().or(z.literal('')),

  // Consent
  consentGiven: z.literal(true, {
    errorMap: () => ({ message: 'You must consent to continue' }),
  }),
});

export type FormSubmissionInput = z.infer<typeof formSubmissionSchema>;

// =============================================================================
// New Western Public Form Schema — same fields, no partner reference
// =============================================================================

export const nwFormSubmissionSchema = formSubmissionSchema
  .omit({ partnerSubmissionId: true })
  .extend({
    // Honeypot: hidden field humans never fill. Permissive on purpose — a
    // validation error here would tip off bots; the handler silently drops
    // submissions where it's non-empty.
    website: z.string().max(500).optional().or(z.literal('')),
    // Cloudflare Turnstile response token. Optional at the schema level so
    // the form keeps working when Turnstile is not configured; the handler
    // requires and verifies it whenever Turnstile keys are present.
    turnstileToken: z.string().max(2048).optional().or(z.literal('')),
  });

export type NwFormSubmissionInput = z.infer<typeof nwFormSubmissionSchema>;

// =============================================================================
// Disbursement Request Schema — "Left Main Pop-Up" (API key required)
// =============================================================================

export const apiSubmissionSchema = z.object({
  // Partner context (set from API key / config)
  partnerName: z.string().min(1, 'Partner name is required'),
  partnerSubmissionId: z.string().min(1, 'Partner submission ID is required'),

  // Property
  propertyAddress1: z.string().min(1, 'Property address is required').max(200),
  propertyAddress2: z.string().max(200).optional().or(z.literal('')),
  propertyCity: z.string().min(1, 'Property city is required').max(100),
  propertyState: z.string().length(2, 'Property state is required'),
  propertyZip: z.string().min(5, 'Property ZIP is required').max(10),

  // Funding
  contractPurchasePrice: z.number().positive('Contract purchase price must be positive'),
  fundAmountRequested: z.number().positive('Fund amount must be positive'),
  estimatedClosingDate: z.string().min(1, 'Estimated closing date is required'),

  // Front Side Title Company
  frontTitleCompanyName: z.string().min(1, 'Front side title company name is required').max(200),
  frontTitleContactName: z.string().min(1, 'Front side contact name is required').max(200),
  frontTitleContactEmail: z.string().email('Valid front side contact email is required'),

  // Back Side Title Company
  backTitleCompanyName: z.string().min(1, 'Back side title company name is required').max(200),
  backTitleContactName: z.string().min(1, 'Back side contact name is required').max(200),
  backTitleContactEmail: z.string().email('Valid back side contact email is required'),
});

export type ApiSubmissionInput = z.infer<typeof apiSubmissionSchema>;
